/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useQuery } from 'react-query'

import { getBudgetV1 } from '@modules/billing-api/v1/callers'
import type { operations } from '@modules/billing-api/v1/types'
import type { QueryHookOptions } from '@modules/query/types'
import { queryClient } from '@modules/query'

import { defaultBillingOptions } from './constants'

const QUERY_KEY = 'getBudgetV1'

type GetBudgetVariables = {
  organizationId: operations['getBudgetV1']['parameters']['path']['organization_id']
  budgetId: string
}

const useGetBudget = (
  organizationId?: GetBudgetVariables['organizationId'],
  budgetId?: GetBudgetVariables['budgetId'],
  options?: QueryHookOptions,
) =>
  useQuery(
    [QUERY_KEY, organizationId, budgetId?.toString()],
    () =>
      getBudgetV1({
        pathParameters: { organization_id: organizationId!, budget_id: Number(budgetId!) },
      }),
    {
      ...defaultBillingOptions,
      ...options,
      enabled: Boolean(organizationId && budgetId),
    },
  )

export const invalidateGetBudget = (
  organizationId?: GetBudgetVariables['organizationId'],
  budgetId?: GetBudgetVariables['budgetId'],
) => {
  const keysToInvalidate = [QUERY_KEY]

  if (organizationId) {
    keysToInvalidate.push(organizationId)
  }

  if (budgetId) {
    keysToInvalidate.push(budgetId.toString())
  }

  return queryClient.invalidateQueries(keysToInvalidate)
}

export default useGetBudget
